import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import React, { useContext, useState } from 'react';

import { StatusCode } from '../../../../../../api/enumerations';
import { updateAnnouncement } from '../../../../../../api/theHive/announcement';
import { AnnouncementData } from '../../../../../../api/theHive/announcement/types';
import {
  IconVisibilityMS,
  IconVisibilityOffMS,
} from '../../../../../../constants/icons';
import { GlobalContext } from '../../../../../../context/global';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import ConfirmationVisibilityDialog from '../ConfirmationVisibilityDialog';
import { ViewAnnouncementsModal } from '../ViewAnnouncementsModal';
import {
  TableCellHeader,
  StyledCell,
  StyledRow,
  StyledPagination,
  StyledCellIcon,
  StyledIcon,
} from './styles';

interface AnnouncementTableProps {
  data: AnnouncementData[];
  page: number;
  totalPages: number;
  setPage: (value: number) => void;
  onAnnouncementUpdated: () => Promise<void>;
}

export function AnnouncementTable({
  data,
  page,
  totalPages,
  setPage,
  onAnnouncementUpdated,
}: AnnouncementTableProps): JSX.Element {
  const [showViewAnnouncementModal, setShowViewAnnouncementModal] =
    useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] =
    useState<AnnouncementData | null>(null);
  const [openVisibility, setOpenVisibility] = useState(false);

  const { setOpenSnackbar, setErrorMessage, setSnackbarMessage } =
    useContext(GlobalContext);
  const { getErrorMessage } = useErrorMessage();

  const handleClose = (): void => setOpenVisibility(false);
  const handleOpen = (announcement: AnnouncementData): void => {
    setSelectedAnnouncement(announcement);
    setOpenVisibility(true);
  };

  const handleShowViewAnnouncementModal = (
    announcement: AnnouncementData
  ): void => {
    setSelectedAnnouncement(announcement);
    setShowViewAnnouncementModal(true);
  };

  const handleCloseViewAnnouncementModal = (): void => {
    setShowViewAnnouncementModal(false);
    setSelectedAnnouncement(null);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ): void => {
    setPage(value);
  };

  const handleAnnouncement = async (): Promise<void> => {
    if (!selectedAnnouncement) return;

    try {
      const response = await updateAnnouncement(selectedAnnouncement.id, {
        is_visible: !selectedAnnouncement.is_visible,
      });

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente.');

      setSnackbarMessage('Visibilidade alterada com sucesso!');
      setErrorMessage(false);
      setOpenSnackbar(true);
      onAnnouncementUpdated();
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    }
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCellHeader colSpan={2}>Comunicados</TableCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 && (
              <StyledRow>
                <StyledCell colSpan={2} align="center">
                  Nenhum comunicado cadastrado.
                </StyledCell>
              </StyledRow>
            )}
            {data.map((item) => (
              <StyledRow key={`row-${item.id}`}>
                <StyledCell
                  width="90%"
                  onClick={() => handleShowViewAnnouncementModal(item)}
                  sx={{ cursor: 'pointer' }}
                >
                  {item.subject}
                </StyledCell>
                <StyledCellIcon>
                  <Tooltip
                    title={
                      item.is_visible
                        ? 'omitir comunicado'
                        : 'exibir comunicado'
                    }
                  >
                    <StyledIcon onClick={() => handleOpen(item)}>
                      {item.is_visible ? IconVisibilityOffMS : IconVisibilityMS}
                    </StyledIcon>
                  </Tooltip>
                </StyledCellIcon>
              </StyledRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <StyledPagination
        page={page}
        count={totalPages}
        onChange={handlePageChange}
      />
      {selectedAnnouncement && (
        <ViewAnnouncementsModal
          title={selectedAnnouncement.subject}
          announcement={selectedAnnouncement}
          onCloseModal={handleCloseViewAnnouncementModal}
          isModalOpen={showViewAnnouncementModal}
        />
      )}
      {openVisibility && (
        <ConfirmationVisibilityDialog
          text={selectedAnnouncement?.is_visible ? 'omitir' : 'exibir'}
          modalCallback={handleAnnouncement}
          open={openVisibility}
          handleClose={handleClose}
        />
      )}
    </>
  );
}

export default AnnouncementTable;
